/**
 * FormsExternalService
 * External integrator API authenticated with a managed API key (`X-API-Key`).
 * Keys need AllowedServices ∋ "forms-management", the Forms.External.* rights and a
 * per-form grant (FormApiAccess) unless the key has an all-forms grant.
 * Route: forms/api/v1/external/forms
 */
import { BaseService } from '../base/BaseService';
import { unwrapFormsResponse as unwrap } from './unwrapFormsResponse';
/** External (API-key) forms service. */
export class FormsExternalService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? FormsExternalService.DEFAULT_SERVICE_PATH,
            basePath: FormsExternalService.BASE_PATH,
            endpoints: config?.endpoints,
        });
        this.apiKey = config?.apiKey;
    }
    /** Set (or clear) the API key used for subsequent calls. */
    setApiKey(apiKey) {
        this.apiKey = apiKey;
    }
    options(extra) {
        const headers = { ...(extra ?? {}) };
        if (this.apiKey)
            headers['X-API-Key'] = this.apiKey;
        return Object.keys(headers).length ? { headers } : undefined;
    }
    seg(value) {
        return encodeURIComponent(String(value));
    }
    // ---- forms ----
    /** GET api/v1/external/forms — forms this key may use (External.Read). */
    async listForms() {
        return (await this.listFormsRaw()).data ?? [];
    }
    listFormsRaw() {
        return this.http.get(this.buildUrl(), this.options());
    }
    /** GET api/v1/external/forms/{idOrCode} (External.Read). */
    async getForm(idOrCode) {
        return unwrap(await this.getFormRaw(idOrCode), `Form ${idOrCode} not found`);
    }
    getFormRaw(idOrCode) {
        return this.http.get(this.buildUrl(this.seg(idOrCode)), this.options());
    }
    /** GET api/v1/external/forms/{idOrCode}/lookups/{locations|departments} (External.Read). */
    async getLookups(idOrCode, kind) {
        return (await this.getLookupsRaw(idOrCode, kind)).data ?? [];
    }
    getLookupsRaw(idOrCode, kind) {
        return this.http.get(this.buildUrl(`${this.seg(idOrCode)}/lookups/${kind}`), this.options());
    }
    // ---- submit ----
    /** POST api/v1/external/forms/{idOrCode}/validate (External.Submit). */
    async validate(idOrCode, request) {
        return unwrap(await this.validateRaw(idOrCode, request), `Failed to validate entry for ${idOrCode}`);
    }
    validateRaw(idOrCode, request) {
        return this.http.post(this.buildUrl(`${this.seg(idOrCode)}/validate`), request, this.options());
    }
    /** POST api/v1/external/forms/{idOrCode}/files/presign (External.Submit). */
    async presign(idOrCode, request) {
        return unwrap(await this.presignRaw(idOrCode, request), `Failed to presign upload for ${idOrCode}`);
    }
    presignRaw(idOrCode, request) {
        return this.http.post(this.buildUrl(`${this.seg(idOrCode)}/files/presign`), request, this.options());
    }
    /**
     * POST api/v1/external/forms/{idOrCode}/entries (External.Submit) with `Idempotency-Key`.
     * Replaying the same key + body returns the original result; a different body → 422.
     */
    async submit(idOrCode, request, idempotencyKey) {
        return unwrap(await this.submitRaw(idOrCode, request, idempotencyKey), `Failed to submit entry for ${idOrCode}`);
    }
    submitRaw(idOrCode, request, idempotencyKey) {
        return this.http.post(this.buildUrl(`${this.seg(idOrCode)}/entries`), request, this.options(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined));
    }
    // ---- entries ----
    /** GET api/v1/external/forms/{idOrCode}/entries?updatedSince=&cursor=&limit= (External.Write). */
    async listEntries(idOrCode, query = {}) {
        return unwrap(await this.listEntriesRaw(idOrCode, query), `Entries for ${idOrCode} not found`);
    }
    listEntriesRaw(idOrCode, query = {}) {
        return this.http.get(this.buildUrlWithParams(`${this.seg(idOrCode)}/entries`, {
            updatedSince: query.updatedSince,
            cursor: query.cursor,
            limit: query.limit,
        }), this.options());
    }
    /** GET api/v1/external/forms/{idOrCode}/entries.csv?updatedSince= (External.Write) — raw text/csv. */
    exportEntriesCsv(idOrCode, updatedSince) {
        return this.http.getBlob(this.buildUrlWithParams(`${this.seg(idOrCode)}/entries.csv`, { updatedSince }), this.options());
    }
    /** GET api/v1/external/forms/entries/{ref} (External.Submit; own submissions unless CanReadEntries). */
    async getEntry(ref) {
        return unwrap(await this.getEntryRaw(ref), `Entry ${ref} not found`);
    }
    getEntryRaw(ref) {
        return this.http.get(this.buildUrl(`entries/${this.seg(ref)}`), this.options());
    }
    /** PUT api/v1/external/forms/entries/{ref} (External.Submit). */
    async updateEntry(ref, request) {
        return unwrap(await this.updateEntryRaw(ref, request), `Failed to update entry ${ref}`);
    }
    updateEntryRaw(ref, request) {
        return this.http.put(this.buildUrl(`entries/${this.seg(ref)}`), request, this.options());
    }
    /** POST api/v1/external/forms/entries/{ref}/cancel (External.Submit). */
    async cancelEntry(ref) {
        return unwrap(await this.cancelEntryRaw(ref), `Failed to cancel entry ${ref}`);
    }
    cancelEntryRaw(ref) {
        return this.http.post(this.buildUrl(`entries/${this.seg(ref)}/cancel`), {}, this.options());
    }
    /** PATCH api/v1/external/forms/entries/{ref}/status (External.Write). */
    async changeStatus(ref, request) {
        return unwrap(await this.changeStatusRaw(ref, request), `Failed to change status of entry ${ref}`);
    }
    changeStatusRaw(ref, request) {
        return this.http.patch(this.buildUrl(`entries/${this.seg(ref)}/status`), request, this.options());
    }
    // ---- webhooks ----
    /** GET api/v1/external/forms/webhooks (External.Write) — this key's subscriptions. */
    async listWebhooks() {
        return (await this.listWebhooksRaw()).data ?? [];
    }
    listWebhooksRaw() {
        return this.http.get(this.buildUrl('webhooks'), this.options());
    }
    /** POST api/v1/external/forms/webhooks (External.Write) — the secret is returned once. */
    async createWebhook(request) {
        return unwrap(await this.createWebhookRaw(request), 'Failed to create webhook');
    }
    createWebhookRaw(request) {
        return this.http.post(this.buildUrl('webhooks'), request, this.options());
    }
    /** DELETE api/v1/external/forms/webhooks/{id} (External.Write). */
    async deleteWebhook(id) {
        const response = await this.deleteWebhookRaw(id);
        return response.data ?? response.success;
    }
    deleteWebhookRaw(id) {
        return this.http.delete(this.buildUrl(`webhooks/${id}`), this.options());
    }
}
FormsExternalService.DEFAULT_SERVICE_PATH = 'forms';
FormsExternalService.BASE_PATH = 'api/v1/external/forms';
//# sourceMappingURL=FormsExternalService.js.map